import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authClient } from '../lib/auth-client';
import { clearHadSession } from '../lib/sessionDiagnostics';
import { isNative, redirectToSignIn } from '../lib/platform';

/**
 * Permanently deletes the signed-in user's OWN account via better-auth's
 * /delete-user endpoint (App Store 5.1.1(v)). The server cancels any Lemon
 * Squeezy subscription, removes all user data, and revokes every session;
 * credential users must supply their password, social users need a recent
 * sign-in. (Distinct from useDeleteAccount in useLinkedAccounts, which
 * disconnects a linked brokerage account.)
 *
 * Errors stay on mutation.error for in-dialog display — the account still
 * exists on failure, so there is no redirect in that path.
 */
export const useDeleteUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (password?: string) => {
      const { error } = await authClient.deleteUser(password ? { password } : {});
      if (error) {
        throw new Error(error.message ?? 'Failed to delete your account');
      }
    },
    onSuccess: async () => {
      // Same cross-user hygiene as useLogout, but with a HARD redirect: the
      // session is revoked server-side, yet the better-auth client store
      // still holds it in memory — an SPA navigate would let GuestOnlyRoute
      // bounce the "logged-in" ghost back to /dashboard. A full reload
      // clears every in-memory trace. (Native's redirectToSignIn already
      // reloads from the app root and clears the bearer token.)
      queryClient.clear();
      clearHadSession();
      if (isNative()) {
        await redirectToSignIn();
        return;
      }
      window.location.href = '/sign-in';
    },
  });
};
